import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Menu,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { apiFetch } from "../../services/api/Navbar";

/* ================= Interfaces ================= */
interface MenuItem {
  id?: number;
  title?: string;
  label?: string;
  url?: string;
  path?: string;
  children?: MenuItem[];
}

/* ================= Static Items ================= */
const STATIC_ITEMS: MenuItem[] = [
  {
    label: "HOME",
    path: "/",
  },
];

/* ================= Helper ================= */
const mapBackendMenu = (items: any[]): MenuItem[] => {
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    label: item.title,
    url: item.url || "#",
    children: item.children ? mapBackendMenu(item.children) : [],
  }));
};

/* ================= Component ================= */
export function Header() {
  const location = useLocation();

  /* ================= Check Home Page ================= */
  const isHomePage = location.pathname === "/";

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState("");

  /* ================= Sticky Scroll ================= */
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* ================= Mobile Menu ================= */
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<number | null>(
    null,
  );

  const [mobileSubSubmenuOpen, setMobileSubSubmenuOpen] = useState<
    number | null
  >(null);

  /* ================= Fetch Menu ================= */
  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);

      try {
        const res = await apiFetch("menu");
        if (res?.data?.success || res?.data?.status === 200) {
          const backendItems = res?.data?.data?.items || [];

          const dynamicMenu = mapBackendMenu(backendItems);

          const backendLogo = res?.data?.data?.settings?.logo;

          if (backendLogo) {
            setLogo(backendLogo);
          }

          setMenuItems([...STATIC_ITEMS, ...dynamicMenu]);
        }
      } catch (err) {
        console.error("Menu fetch error", err);

        setMenuItems(STATIC_ITEMS);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  /* ================= Navbar Style Logic ================= */

  // HOME PAGE
  // top => transparent
  // scroll => white

  // OTHER PAGES
  // always white

  const navbarClass = isHomePage
    ? isScrolled
      ? "bg-white shadow-lg backdrop-blur-md"
      : "bg-transparent"
    : "bg-white shadow-md";

  const textClass =
    isHomePage && !isScrolled
      ? "text-white hover:text-yellow-300"
      : "text-gray-800 hover:text-blue-600";

  const buttonClass =
    isHomePage && !isScrolled
      ? "bg-white text-black hover:bg-yellow-300"
      : "bg-blue-900 text-white hover:bg-blue-700";

  return (
    <header
      className={`
        fixed top-0 left-0 w-full z-50 py-2
        transition-all duration-500 ease-in-out
        ${navbarClass}
      `}
    >
      <div className="w-full mx-auto px-3 lg:px-6">
        <div className="flex items-center justify-between h-24">
          {/* ================= Logo ================= */}
          <Link to="/">
            <img
              src={logo || "/images/logo.png"}
              alt="Logo"
              className={`
                object-contain transition-all duration-500
                ${isScrolled ? "h-16 w-16" : "h-24 w-24"}
              `}
            />
          </Link>

          {/* ================= Desktop Menu ================= */}
          <nav className="hidden lg:flex items-center gap-6">
            {loading ? (
              <span className={textClass}>Loading...</span>
            ) : (
              menuItems.map((item) => (
                <div key={item.id || item.label} className="relative group">
                  {/* Main Menu */}
                  <Link
                    to={item.path || item.url || "#"}
                    className={`
                      flex items-center gap-1
                      font-bold uppercase text-sm
                      transition-all duration-300
                      ${textClass}
                    `}
                  >
                    {item.title || item.label}

                    {item.children?.length ? (
                      <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
                    ) : null}
                  </Link>

                  {/* ================= Dropdown ================= */}
                  {item.children?.length ? (
                    <div
                      className="
                        absolute left-0 top-full mt-5
                        w-56 bg-white rounded-lg shadow-xl
                        opacity-0 invisible
                        group-hover:opacity-100
                        group-hover:visible
                        transition-all duration-300
                        z-50
                      "
                    >
                      {item.children.map((sub) => (
                        <div
                          key={sub.id || sub.label}
                          className="relative group/sub"
                        >
                          <Link
                            to={sub.url || "#"}
                            className="
                                flex items-center justify-between
                                px-4 py-3 text-sm font-semibold
                                text-gray-700
                                hover:bg-blue-50
                                hover:text-blue-600
                              "
                          >
                            {sub.title}

                            {sub.children?.length ? (
                              <ChevronRight className="w-4 h-4" />
                            ) : null}
                          </Link>

                          {/* Level 2 */}
                          {sub.children?.length ? (
                            <div
                              className="
                                  absolute top-0 left-full
                                  w-56 bg-white rounded-lg shadow-xl
                                  opacity-0 invisible
                                  group-hover/sub:opacity-100
                                  group-hover/sub:visible
                                  transition-all duration-300
                                "
                            >
                              {sub.children.map((third) => (
                                <Link
                                  key={third.id || third.label}
                                  to={third.url || "#"}
                                  className="
                                        block px-4 py-3
                                        text-sm font-semibold
                                        text-gray-700
                                        hover:bg-blue-50
                                        hover:text-blue-600
                                      "
                                >
                                  {third.title}
                                </Link>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </nav>

          {/* ================= Right Side ================= */}
          <div className="flex items-center gap-4">
            {/* Register Button */}
            <Link to="/athletes/register" className="hidden lg:block">
              <button
                className={`
                  flex items-center gap-2 whitespace-nowrap
                  px-5 py-2 rounded-md
                  font-semibold transition-all duration-300
                  ${buttonClass}
                `}
              >
                Register Now
                <ArrowRight size={18} />
              </button>
            </Link>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              className={`
    lg:hidden
    h-16 w-16 p-0
    ${isHomePage && !isScrolled ? "text-white" : "text-black"}
  `}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-12 w-12" />
            </Button>
          </div>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg border-t max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col">
            {menuItems.map((item) => (
              <div key={item.id || item.label}>
                {/* Parent Item Without Children */}
                {!item.children?.length ? (
                  <Link
                    to={item.path || item.url || "#"}
                    className="flex items-center justify-between px-6 py-4 font-semibold text-gray-700 border-b"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.title || item.label}
                  </Link>
                ) : (
                  <>
                    {/* Parent Menu */}
                    <button
                      className="w-full flex items-center justify-between px-6 py-4 font-semibold text-gray-700 border-b"
                      onClick={() =>
                        setMobileSubmenuOpen(
                          mobileSubmenuOpen === item.id
                            ? null
                            : item.id || null,
                        )
                      }
                    >
                      <span>{item.title || item.label}</span>

                      {mobileSubmenuOpen === item.id ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </button>

                    {/* First Level Children */}
                    {mobileSubmenuOpen === item.id && (
                      <div className="bg-gray-50">
                        {item.children.map((sub) => (
                          <div key={sub.id}>
                            {/* Child with grandchildren */}
                            {sub.children?.length ? (
                              <>
                                <button
                                  className="w-full flex items-center justify-between px-10 py-3 text-sm font-medium text-gray-600 border-b"
                                  onClick={() =>
                                    setMobileSubSubmenuOpen(
                                      mobileSubSubmenuOpen === sub.id
                                        ? null
                                        : sub.id || null,
                                    )
                                  }
                                >
                                  <span>{sub.title}</span>

                                  {mobileSubSubmenuOpen === sub.id ? (
                                    <ChevronUp size={16} />
                                  ) : (
                                    <ChevronDown size={16} />
                                  )}
                                </button>

                                {mobileSubSubmenuOpen === sub.id && (
                                  <div className="bg-gray-100">
                                    {sub.children.map((third) => (
                                      <Link
                                        key={third.id}
                                        to={third.url || "#"}
                                        className="block px-14 py-3 text-sm text-gray-700 border-b hover:bg-gray-200"
                                        onClick={() => setMobileMenuOpen(false)}
                                      >
                                        {third.title}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              /* Direct Child Link */
                              <Link
                                to={sub.url || "#"}
                                className="block px-10 py-3 text-sm text-gray-600 border-b hover:bg-gray-100"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {sub.title}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
